/**
 * Extracts thinking content from a message wrapped in <think> tags
 * @param content The full message content
 * @returns Object with thinking content and cleaned content
 */
export const extractThinking = (content: string): { thinking: string, content: string } => {
    // Match any content between opening and closing think tags
    const thinkingRegex = /<think>([\s\S]*?)<\/think>/g;
    let thinking = '';

    // Find all complete thinking blocks
    const matches = Array.from(content.matchAll(thinkingRegex));
    let cleanContent = content;

    if (matches && matches.length > 0) {
        // Extract all thinking content from complete tags
        matches.forEach(match => {
            if (match && match[0] && match[1]) {
                const innerContent = match[1].trim();
                thinking += (thinking ? '\n\n' : '') + innerContent;
                // Remove thinking blocks from the main content
                cleanContent = cleanContent.replace(match[0], '');
            }
        });

        // Clean up any additional whitespace
        cleanContent = cleanContent.trim();

        return { thinking, content: cleanContent };
    }

    // Even if we don't have complete tags, we need to provide a clean version 
    // of content that removes any partial thinking tags
    const cleanedPartialContent = content
        .replace(/<think>[\s\S]*?<\/think>/g, '') // Remove complete tags if any
        .replace(/<think>[\s\S]*$/g, '')          // Remove incomplete opening tags
        .replace(/^[\s\S]*?<\/think>/g, '')       // Remove incomplete closing tags
        .trim();

    // Only return thinking content if we have complete tags
    return { thinking, content: cleanedPartialContent };
};

/**
 * Formats code blocks with syntax highlighting
 * @param text Text containing code blocks
 * @returns Formatted text with highlighted code blocks
 */
export const formatCodeBlocks = (text: string): string => {
    // This is a placeholder - would be implemented with a code highlighting library
    return text;
};

/**
 * Truncates text to a specified length
 * @param text Text to truncate
 * @param maxLength Maximum length
 * @returns Truncated text with ellipsis
 */
export const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
};

/**
 * Clean redaction blocks from text content, converting them to readable placeholders.
 * This helps when content has black blocks (█) that make text unreadable.
 * 
 * @param content The content string that may contain redaction blocks
 * @returns Cleaned text with redaction blocks replaced by meaningful placeholders
 */
export const cleanRedactionBlocks = (content: string): string => {
    if (!content) return '';

    return content
        // Replace redaction blocks in algorithm descriptions
        .replace(/(\d+\.\s+.+?)█+/g, (_, prefix) => {
            // These are often variable names or values
            return `${prefix}value`;
        })
        // Replace redaction blocks in code examples
        .replace(/(let|const|var|function)\s+█+/g, '$1 variable')
        // Replace standalone redaction blocks
        .replace(/█+/g, 'value')
        // Replace black square blocks
        .replace(/⬛+/g, 'value')
        // Replace blocks at the beginning of lines (often placeholders in algorithms)
        .replace(/^(\s*)█+/gm, '$1placeholder')
        // Replace blocks in position notation
        .replace(/position\s+█+/gi, 'position i')
        // Clean up the mess of multiple adjacent placeholders
        .replace(/(value|placeholder)\s+(value|placeholder)/g, '$1');
}; 